import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, CheckCircle, Check, Printer } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { collection, addDoc, getDocs, doc, updateDoc,
  deleteDoc, query, getDoc, writeBatch } from 'firebase/firestore';
import { QRCodeCanvas as QRCode } from 'qrcode.react';
import imageCompression from 'browser-image-compression';
import { translateAll } from '../utils/translate';

const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

const PLAN_LIMITS = {
  free: { categoryLimit: 0, tableLimit: 0, menuLimit: 10 },
  standard: { categoryLimit: Infinity, tableLimit: Infinity, menuLimit: 300 },
  premium: { categoryLimit: Infinity, tableLimit: Infinity, menuLimit: 300 },
  pro: { categoryLimit: Infinity, tableLimit: Infinity, menuLimit: 300 },
};

const emptyForm = {
  nameJa: '', nameEn: '', nameZhCN: '', nameZhTW: '', nameKo: '',
  descJa: '', descEn: '', descZhCN: '', descZhTW: '', descKo: '',
  price: '', status: 'available', imageUrl: '', categoryId: '', menuNumber: '',
};

function MenuEditor() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [menus, setMenus] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tables, setTables] = useState([]);
  const [storeId, setStoreId] = useState('');
  const [plan, setPlan] = useState('free');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [newCatName, setNewCatName] = useState('');
  const [catAdding, setCatAdding] = useState(false);
  const [showCatSection, setShowCatSection] = useState(false);
  const [showTableSection, setShowTableSection] = useState(false);
  const [newTableName, setNewTableName] = useState('');
  const [tableAdding, setTableAdding] = useState(false);
  const [selectedTableQR, setSelectedTableQR] = useState(null);
  const [editingTableId, setEditingTableId] = useState(null);
  const [editingTableName, setEditingTableName] = useState('');
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvResult, setCsvResult] = useState('');
  const csvInputRef = useRef(null);

  const isStandard = plan === 'standard' || plan === 'premium' || plan === 'pro';
  const catLimit = PLAN_LIMITS[plan]?.categoryLimit ?? 0;
  const tableLimit = PLAN_LIMITS[plan]?.tableLimit ?? 0;
  const menuLimit = PLAN_LIMITS[plan]?.menuLimit ?? 10;

  useEffect(() => { fetchData(); }, []);

  async function fetchData() {
    const storeDoc = await getDoc(doc(db, 'stores', currentUser.uid));
    if (storeDoc.exists()) {
      const data = storeDoc.data();
      setStoreId(data.storeId || currentUser.uid);
      setPlan(data.plan || 'free');
    }
    const q = query(collection(db, 'stores', currentUser.uid, 'menus'));
    const snapshot = await getDocs(q);
    const menuData = snapshot.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
    setMenus(menuData);

    const catsSnap = await getDocs(collection(db, 'stores', currentUser.uid, 'categories'));
    const cats = catsSnap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    setCategories(cats);

    const tablesSnap = await getDocs(collection(db, 'stores', currentUser.uid, 'tables'));
    const tableData = tablesSnap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    setTables(tableData);
  }

  async function handleTranslate() {
    if (!form.nameJa) return;
    setTranslating(true);
    const result = await translateAll(form.nameJa, form.descJa, isStandard);
    setForm(prev => ({ ...prev, ...result }));
    setTranslating(false);
  }

  async function handleImageUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const options = { maxSizeMB: 0.2, maxWidthOrHeight: 800, useWebWorker: true };
      const compressed = await imageCompression(file, options);
      const formData = new FormData();
      formData.append('file', compressed);
      formData.append('upload_preset', UPLOAD_PRESET);
      formData.append('folder', `menusore/${currentUser.uid}`);
      const res = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
        { method: 'POST', body: formData }
      );
      const data = await res.json();
      setForm(prev => ({ ...prev, imageUrl: data.secure_url }));
      setImagePreview(data.secure_url);
    } catch { alert('画像のアップロードに失敗しました'); }
    setUploading(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    const data = {
      nameJa: form.nameJa || '', nameEn: form.nameEn || '',
      nameZhCN: form.nameZhCN || '', nameZhTW: form.nameZhTW || '',
      nameKo: form.nameKo || '',
      descJa: form.descJa || '', descEn: form.descEn || '',
      descZhCN: form.descZhCN || '', descZhTW: form.descZhTW || '',
      descKo: form.descKo || '',
      price: Number(form.price),
      status: form.status || 'available',
      menuNumber: Number(form.menuNumber) || 0,
      imageUrl: form.imageUrl || '',
      categoryId: form.categoryId || '',
    };
    if (editingId) {
      const dupEdit = menus.some(m => m.id !== editingId && (m.menuNumber || 0) === Number(form.menuNumber));
      if (form.menuNumber && dupEdit) { alert(`No.${form.menuNumber} はすでに使われています`); setLoading(false); return; }
      await updateDoc(doc(db, 'stores', currentUser.uid, 'menus', editingId), data);
      setEditingId(null);
    } else {
      if (menus.length >= menuLimit) {
        alert(`メニューは${menuLimit}品までです（プランの上限に達しています）`);
        setLoading(false);
        return;
      }
      const maxNum = menus.reduce((max, m) => Math.max(max, m.menuNumber || 0), 0);
      const autoNumber = form.menuNumber ? Number(form.menuNumber) : maxNum + 1;
      const duplicate = menus.some(m => (m.menuNumber || 0) === autoNumber);
      if (duplicate) { alert(`No.${autoNumber} はすでに使われています`); setLoading(false); return; }
      await addDoc(collection(db, 'stores', currentUser.uid, 'menus'), {
        ...data, menuNumber: autoNumber, soldCount: 0, order: menus.length, createdAt: new Date(),
      });
    }
    setForm(emptyForm);
    setImagePreview('');
    await fetchData();
    setLoading(false);
  }

  function handleEdit(menu) {
    setEditingId(menu.id);
    setForm({
      nameJa: menu.nameJa || '', nameEn: menu.nameEn || '',
      nameZhCN: menu.nameZhCN || '', nameZhTW: menu.nameZhTW || '',
      nameKo: menu.nameKo || '',
      descJa: menu.descJa || '', descEn: menu.descEn || '',
      descZhCN: menu.descZhCN || '', descZhTW: menu.descZhTW || '',
      descKo: menu.descKo || '',
      price: menu.price || '', status: menu.status || 'available',
      imageUrl: menu.imageUrl || '', categoryId: menu.categoryId || '', menuNumber: menu.menuNumber || '',
    });
    setImagePreview(menu.imageUrl || '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleCancelEdit() {
    setEditingId(null);
    setForm(emptyForm);
    setImagePreview('');
  }

  async function handleSoldCount(menu) {
    await updateDoc(doc(db, 'stores', currentUser.uid, 'menus', menu.id), {
      soldCount: (menu.soldCount || 0) + 1,
    });
    await fetchData();
  }

  async function handleStatus(menu) {
    const newStatus = menu.status === 'available' ? 'soldout' : 'available';
    await updateDoc(doc(db, 'stores', currentUser.uid, 'menus', menu.id), { status: newStatus });
    await fetchData();
  }

  async function handleDelete(id) {
    if (window.confirm('削除しますか？')) {
      await deleteDoc(doc(db, 'stores', currentUser.uid, 'menus', id));
      await fetchData();
    }
  }

  async function moveMenu(index, direction) {
    const newMenus = [...menus];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newMenus.length) return;
    [newMenus[index], newMenus[targetIndex]] = [newMenus[targetIndex], newMenus[index]];
    const batch = writeBatch(db);
    newMenus.forEach((menu, i) => {
      batch.update(doc(db, 'stores', currentUser.uid, 'menus', menu.id), { order: i });
    });
    await batch.commit();
    setMenus(newMenus);
  }

  // ---- CSVインポート/エクスポート ----
  function downloadCsv(filename, rows) {
    const csv = rows.map(row =>
      row.map(cell => {
        const s = String(cell ?? '');
        return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
      }).join(',')
    ).join('\r\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function parseCsv(text) {
    const rows = [];
    let row = [];
    let field = '';
    let inQuotes = false;
    const cleanText = text.replace(/^\ufeff/, '');
    for (let i = 0; i < cleanText.length; i++) {
      const c = cleanText[i];
      if (inQuotes) {
        if (c === '"') {
          if (cleanText[i + 1] === '"') { field += '"'; i++; }
          else inQuotes = false;
        } else field += c;
      } else {
        if (c === '"') inQuotes = true;
        else if (c === ',') { row.push(field); field = ''; }
        else if (c === '\n' || c === '\r') {
          if (c === '\r' && cleanText[i + 1] === '\n') i++;
          row.push(field); field = '';
          if (row.some(v => v !== '')) rows.push(row);
          row = [];
        } else field += c;
      }
    }
    if (field !== '' || row.length > 0) { row.push(field); rows.push(row); }
    return rows;
  }

  function handleExportMenusCsv() {
    const catName = (id) => categories.find(c => c.id === id)?.nameJa || '';
    const header = ['番号', '商品名', '説明', '価格', 'カテゴリ', 'ステータス'];
    const rows = [header, ...menus.map(m => [
      m.menuNumber || '', m.nameJa || '', m.descJa || '', m.price || 0,
      catName(m.categoryId), m.status === 'soldout' ? '売り切れ' : '販売中',
    ])];
    downloadCsv(`menusore_menus_${storeId}.csv`, rows);
  }

  function handleDownloadCsvTemplate() {
    const rows = [
      ['番号', '商品名', '説明', '価格', 'カテゴリ', 'ステータス'],
      ['1', 'ゴーヤーチャンプルー', '沖縄の定番野菜炒め', '880', 'メイン', '販売中'],
      ['2', 'オリオンビール', '', '550', 'ドリンク', '販売中'],
    ];
    downloadCsv('menusore_import_template.csv', rows);
  }

  async function handleImportCsvFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    setCsvResult('');
    setCsvImporting(true);
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (rows.length < 2) { setCsvResult('データ行が見つかりませんでした'); setCsvImporting(false); return; }
      const header = rows[0].map(h => h.trim());
      const idx = {
        number: header.indexOf('番号'),
        name: header.indexOf('商品名'),
        desc: header.indexOf('説明'),
        price: header.indexOf('価格'),
        category: header.indexOf('カテゴリ'),
        status: header.indexOf('ステータス'),
      };
      if (idx.name === -1 || idx.price === -1) {
        setCsvResult('ヘッダーに「商品名」「価格」列が見つかりません。テンプレートをご確認ください。');
        setCsvImporting(false);
        return;
      }
      const dataRows = rows.slice(1).filter(r => (r[idx.name] || '').trim());
      const availableSlots = menuLimit - menus.length;
      if (availableSlots <= 0) {
        setCsvResult(`メニュー上限（${menuLimit}品）に達しているため追加できません`);
        setCsvImporting(false);
        return;
      }
      const toImport = dataRows.slice(0, availableSlots);
      const skipped = dataRows.length - toImport.length;

      // カテゴリ名 → categoryId（なければ新規作成、Freeプランはカテゴリなし扱い）
      const catMap = {};
      categories.forEach(c => { catMap[c.nameJa.trim()] = c.id; });
      let currentCategories = [...categories];

      let maxNum = menus.reduce((max, m) => Math.max(max, m.menuNumber || 0), 0);
      const usedNumbers = new Set(menus.map(m => m.menuNumber || 0));
      let created = 0;
      let orderOffset = menus.length;

      for (const r of toImport) {
        const nameJa = (r[idx.name] || '').trim();
        const descJa = idx.desc !== -1 ? (r[idx.desc] || '').trim() : '';
        const price = Number((r[idx.price] || '0').replace(/[^0-9.]/g, '')) || 0;
        const catName = idx.category !== -1 ? (r[idx.category] || '').trim() : '';
        const statusText = idx.status !== -1 ? (r[idx.status] || '').trim() : '';
        const status = statusText.includes('売') ? 'soldout' : 'available';
        let requestedNum = idx.number !== -1 ? Number(r[idx.number]) || 0 : 0;

        let categoryId = '';
        if (catName && isStandard) {
          if (catMap[catName]) {
            categoryId = catMap[catName];
          } else if (currentCategories.length < catLimit) {
            const result = await translateAll(catName, '', true);
            const newCatRef = await addDoc(collection(db, 'stores', currentUser.uid, 'categories'), {
              nameJa: catName,
              nameEn: result.nameEn || '', nameZhCN: result.nameZhCN || '',
              nameZhTW: result.nameZhTW || '', nameKo: result.nameKo || '',
              order: currentCategories.length,
              createdAt: new Date(),
            });
            categoryId = newCatRef.id;
            catMap[catName] = categoryId;
            currentCategories = [...currentCategories, { id: categoryId, nameJa: catName }];
          }
        }

        let menuNumber = requestedNum && !usedNumbers.has(requestedNum) ? requestedNum : (maxNum = maxNum + 1);
        usedNumbers.add(menuNumber);

        const result = await translateAll(nameJa, descJa, isStandard);
        await addDoc(collection(db, 'stores', currentUser.uid, 'menus'), {
          nameJa, nameEn: result.nameEn || '', nameZhCN: result.nameZhCN || '',
          nameZhTW: result.nameZhTW || '', nameKo: result.nameKo || '',
          descJa, descEn: result.descEn || '', descZhCN: result.descZhCN || '',
          descZhTW: result.descZhTW || '', descKo: result.descKo || '',
          price, status, imageUrl: '', categoryId, menuNumber,
          soldCount: 0, order: orderOffset, createdAt: new Date(),
        });
        orderOffset++;
        created++;
      }

      await fetchData();
      setCsvResult(
        `${created}件のメニューを登録しました` +
        (skipped > 0 ? `（メニュー上限のため${skipped}件はスキップしました）` : '')
      );
    } catch (err) {
      console.error(err);
      setCsvResult('インポート中にエラーが発生しました: ' + err.message);
    }
    setCsvImporting(false);
    if (csvInputRef.current) csvInputRef.current.value = '';
  }

  async function handleAddCategory() {
    if (!newCatName) return;
    if (categories.length >= catLimit) { alert(`カテゴリは${catLimit}つまでです`); return; }
    setCatAdding(true);
    const result = await translateAll(newCatName, '', true);
    await addDoc(collection(db, 'stores', currentUser.uid, 'categories'), {
      nameJa: newCatName,
      nameEn: result.nameEn || '',
      nameZhCN: result.nameZhCN || '',
      nameZhTW: result.nameZhTW || '',
      nameKo: result.nameKo || '',
      order: categories.length,
      createdAt: new Date(),
    });
    setNewCatName('');
    await fetchData();
    setCatAdding(false);
  }

  async function handleDeleteCategory(catId) {
    if (!window.confirm('カテゴリを削除しますか？')) return;
    await deleteDoc(doc(db, 'stores', currentUser.uid, 'categories', catId));
    const batch = writeBatch(db);
    menus.filter(m => m.categoryId === catId).forEach(m => {
      batch.update(doc(db, 'stores', currentUser.uid, 'menus', m.id), { categoryId: '' });
    });
    await batch.commit();
    await fetchData();
  }

  async function moveCategory(index, direction) {
    const newCats = [...categories];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newCats.length) return;
    [newCats[index], newCats[targetIndex]] = [newCats[targetIndex], newCats[index]];
    const batch = writeBatch(db);
    newCats.forEach((cat, i) => {
      batch.update(doc(db, 'stores', currentUser.uid, 'categories', cat.id), { order: i });
    });
    await batch.commit();
    setCategories(newCats);
  }

  async function handleAddTable() {
    if (!newTableName) return;
    if (tables.length >= tableLimit) { alert(`テーブルは${tableLimit}つまでです`); return; }
    setTableAdding(true);
    await addDoc(collection(db, 'stores', currentUser.uid, 'tables'), {
      name: newTableName,
      order: tables.length,
      createdAt: new Date(),
    });
    setNewTableName('');
    await fetchData();
    setTableAdding(false);
  }

  async function handleDeleteTable(tableId) {
    if (!window.confirm('テーブルを削除しますか？')) return;
    await deleteDoc(doc(db, 'stores', currentUser.uid, 'tables', tableId));
    await fetchData();
    if (selectedTableQR === tableId) setSelectedTableQR(null);
  }

  async function handleSaveTableName(tableId) {
    if (!editingTableName) return;
    await updateDoc(doc(db, 'stores', currentUser.uid, 'tables', tableId), {
      name: editingTableName,
    });
    setEditingTableId(null);
    setEditingTableName('');
    await fetchData();
  }

  async function moveTable(index, direction) {
    const newTables = [...tables];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newTables.length) return;
    [newTables[index], newTables[targetIndex]] = [newTables[targetIndex], newTables[index]];
    const batch = writeBatch(db);
    newTables.forEach((table, i) => {
      batch.update(doc(db, 'stores', currentUser.uid, 'tables', table.id), { order: i });
    });
    await batch.commit();
    setTables(newTables);
  }

  const menuUrl = `${window.location.origin}/menu/${storeId}`;
  const getTableUrl = (table) => `${menuUrl.replace('localhost', '192.168.11.8')}?table=${encodeURIComponent(table.name)}`;

  return (
    <div style={styles.container}>
      <nav style={styles.nav}>
        <h1 style={styles.logo}>MenuSore</h1>
        <button onClick={() => navigate('/dashboard')} style={styles.btnOutline}>
          ← ダッシュボード
        </button>
      </nav>

      <div style={styles.content}>
        <h2 style={styles.heading}>メニュー管理</h2>

        {/* CSVインポート/エクスポート */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h3 style={styles.sectionTitle}>CSVインポート・エクスポート</h3>
          </div>
          <div style={styles.sectionBody}>
            <div style={{display:"flex", flexWrap:"wrap", gap:8, marginBottom:8}}>
              <button onClick={handleExportMenusCsv} style={styles.btnOutline} disabled={menus.length === 0}>
                現在のメニューをCSVエクスポート
              </button>
              <button onClick={handleDownloadCsvTemplate} style={styles.btnOutline}>
                インポート用テンプレートをダウンロード
              </button>
              <button
                onClick={() => csvInputRef.current && csvInputRef.current.click()}
                style={styles.btnPrimary}
                disabled={csvImporting}
              >
                {csvImporting ? 'インポート中...' : 'CSVから一括インポート'}
              </button>
              <input type="file" accept=".csv" ref={csvInputRef} style={{display:"none"}} onChange={handleImportCsvFile} />
            </div>
            <p style={{fontSize:12, color:"#a0aec0", margin:0}}>
              テンプレートの「番号・商品名・説明・価格・カテゴリ・ステータス」列に沿って作成したCSVをインポートできます。既存メニューは上書きされず、新規追加のみ行われます（画像は含まれないため後で個別に設定してください）。
            </p>
            {csvResult && (
              <p style={{fontSize:13, color:"#2d6a4f", marginTop:8, fontWeight:600}}>{csvResult}</p>
            )}
          </div>
        </div>


        {/* カテゴリ管理 */}
        {isStandard && (
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <h3 style={styles.sectionTitle}>カテゴリ管理</h3>
              <button onClick={() => setShowCatSection(!showCatSection)} style={styles.btnToggle}>
                {showCatSection ? '閉じる ▲' : '開く ▼'}
              </button>
            </div>
            {showCatSection && (
              <div style={styles.sectionBody}>
                <div style={styles.catList}>
                  {categories.length === 0 ? (
                    <p style={styles.empty}>カテゴリがありません</p>
                  ) : categories.map((cat, i) => (
                    <div key={cat.id} style={styles.catItem}>
                      <div style={styles.movebtns}>
                        <button onClick={() => moveCategory(i, -1)} disabled={i === 0} style={styles.btnMove}>↑</button>
                        <button onClick={() => moveCategory(i, 1)} disabled={i === categories.length - 1} style={styles.btnMove}>↓</button>
                      </div>
                      <div style={styles.catInfo}>
                        <p style={styles.catName}>{cat.nameJa}</p>
                        <p style={styles.catTrans}>
                          {[cat.nameEn, cat.nameZhCN, cat.nameKo].filter(Boolean).join(' / ')}
                        </p>
                      </div>
                      <button onClick={() => handleDeleteCategory(cat.id)} style={styles.btnDanger}>削除</button>
                    </div>
                  ))}
                </div>
                {categories.length < catLimit && (
                  <div style={styles.addRow}>
                    <input style={styles.input} value={newCatName}
                      onChange={e => setNewCatName(e.target.value)}
                      placeholder="カテゴリ名（例：前菜）"
                      onKeyPress={e => e.key === 'Enter' && handleAddCategory()} />
                    <button onClick={handleAddCategory} style={styles.btnPrimary}
                      disabled={catAdding || !newCatName}>
                      {catAdding ? '追加中...' : '+ 追加'}
                    </button>
                  </div>
                )}
                <p style={styles.hint}>{categories.length}/{catLimit} カテゴリ・追加時に自動翻訳</p>
              </div>
            )}
          </div>
        )}

        {/* テーブル管理 */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h3 style={styles.sectionTitle}>
              テーブル管理
              {!isStandard && <span style={styles.planTag}>Pro以上</span>}
            </h3>
            {isStandard && (
              <button onClick={() => setShowTableSection(!showTableSection)} style={styles.btnToggle}>
                {showTableSection ? '閉じる ▲' : '開く ▼'}
              </button>
            )}
          </div>

          {!isStandard ? (
            <div style={styles.sectionBody}>
              <div style={styles.lockedBox}>
                テーブル別QRはProプラン以上でご利用いただけます
                <button style={styles.btnUpgrade} onClick={() => alert('近日公開予定です！')}>
                  アップグレード →
                </button>
              </div>
            </div>
          ) : showTableSection && (
            <div style={styles.sectionBody}>
              <div style={styles.tableList}>
                {tables.length === 0 ? (
                  <p style={styles.empty}>テーブルがありません。追加してください。</p>
                ) : tables.map((table, i) => (
                  <div key={table.id} style={styles.tableItem}>
                    <div style={styles.movebtns}>
                      <button onClick={() => moveTable(i, -1)} disabled={i === 0} style={styles.btnMove}>↑</button>
                      <button onClick={() => moveTable(i, 1)} disabled={i === tables.length - 1} style={styles.btnMove}>↓</button>
                    </div>

                    <div style={styles.tableInfo}>
                      {editingTableId === table.id ? (
                        <div style={styles.tableEditRow}>
                          <input style={styles.tableInput} value={editingTableName}
                            onChange={e => setEditingTableName(e.target.value)}
                            onKeyPress={e => e.key === 'Enter' && handleSaveTableName(table.id)} />
                          <button onClick={() => handleSaveTableName(table.id)} style={styles.btnSmall}>保存</button>
                          <button onClick={() => setEditingTableId(null)} style={styles.btnSmallOutline}>✕</button>
                        </div>
                      ) : (
                        <div style={styles.tableNameRow}>
                          <span style={styles.tableName}>{table.name}</span>
                          <button onClick={() => {
                            setEditingTableId(table.id);
                            setEditingTableName(table.name);
                          }} style={styles.btnSmallOutline}>✏️</button>
                        </div>
                      )}
                      <p style={styles.tableUrl}>{getTableUrl(table)}</p>
                    </div>

                    <div style={styles.tableActions}>
                      <button
                        onClick={() => setSelectedTableQR(selectedTableQR === table.id ? null : table.id)}
                        style={styles.btnSmall}>
                        {selectedTableQR === table.id ? 'QRを閉じる' : 'QR表示'}
                      </button>
                      <button onClick={() => handleDeleteTable(table.id)} style={styles.btnDanger}>削除</button>
                    </div>
                  </div>
                ))}

                {/* QR表示エリア */}
                {selectedTableQR && tables.find(t => t.id === selectedTableQR) && (
                  <div style={styles.qrDisplay}>
                    <p style={styles.qrLabel}>
                      {tables.find(t => t.id === selectedTableQR)?.name} のQRコード
                    </p>
                    <QRCode
                      value={getTableUrl(tables.find(t => t.id === selectedTableQR))}
                      size={200}
                    />
                    <p style={styles.qrUrl}>
                      {getTableUrl(tables.find(t => t.id === selectedTableQR))}
                    </p>
                    <button onClick={() => window.print()} style={styles.btnOutline}>
                      <Printer size={14} style={{display:"inline",verticalAlign:"middle",marginRight:4}} />印刷
                    </button>
                  </div>
                )}
              </div>

              {tables.length < tableLimit && (
                <div style={styles.addRow}>
                  <input style={styles.input} value={newTableName}
                    onChange={e => setNewTableName(e.target.value)}
                    placeholder={`例：テーブル${tables.length + 1}・カウンター左・テラス1`}
                    onKeyPress={e => e.key === 'Enter' && handleAddTable()} />
                  <button onClick={handleAddTable} style={styles.btnPrimary}
                    disabled={tableAdding || !newTableName}>
                    {tableAdding ? '追加中...' : '+ 追加'}
                  </button>
                </div>
              )}
              <p style={styles.hint}>
                {tables.length}/{tableLimit === Infinity ? '無制限' : tableLimit} テーブル
              </p>
            </div>
          )}
        </div>

        {/* メニュー追加・編集フォーム */}
        <div style={styles.form}>
          <h3 style={styles.subHeading}>
            {editingId ? '✏️ メニューを編集' : '＋ メニューを追加'}
          </h3>
          <form onSubmit={handleSubmit}>
            <div style={styles.row}>
              <div style={styles.field}>
                <label style={styles.label}>料理名（日本語）</label>
                <input style={styles.input} value={form.nameJa}
                  onChange={e => setForm({...form, nameJa: e.target.value})}
                  placeholder="例：ゴーヤチャンプルー" required />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>料理名（英語）</label>
                <input style={styles.input} value={form.nameEn}
                  onChange={e => setForm({...form, nameEn: e.target.value})}
                  placeholder="例：Goya Champuru" />
              </div>
            </div>

            <div style={styles.row}>
              <div style={styles.field}>
                <label style={styles.label}>説明文（日本語）</label>
                <textarea style={styles.textarea} value={form.descJa}
                  onChange={e => setForm({...form, descJa: e.target.value})}
                  placeholder="例：祖母から受け継いだ味" rows={2} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>説明文（英語）</label>
                <textarea style={styles.textarea} value={form.descEn}
                  onChange={e => setForm({...form, descEn: e.target.value})}
                  placeholder="自動翻訳されます" rows={2} />
              </div>
            </div>

            {isStandard && (
              <div style={styles.multiLangBox}>
                <p style={styles.multiLangTitle}><Globe size={14} style={{display:'inline',verticalAlign:'middle',marginRight:4}} />多言語（自動翻訳ボタンで一括入力）</p>
                <div style={styles.multiLangGrid}>
                  <div style={styles.miniField}>
                    <label style={styles.miniLabel}>料理名・簡体字</label>
                    <input style={styles.miniInput} value={form.nameZhCN}
                      onChange={e => setForm({...form, nameZhCN: e.target.value})} placeholder="简体中文" />
                  </div>
                  <div style={styles.miniField}>
                    <label style={styles.miniLabel}>料理名・繁体字</label>
                    <input style={styles.miniInput} value={form.nameZhTW}
                      onChange={e => setForm({...form, nameZhTW: e.target.value})} placeholder="繁體中文" />
                  </div>
                  <div style={styles.miniField}>
                    <label style={styles.miniLabel}>料理名・韓国語</label>
                    <input style={styles.miniInput} value={form.nameKo}
                      onChange={e => setForm({...form, nameKo: e.target.value})} placeholder="한국어" />
                  </div>
                  <div style={styles.miniField}>
                    <label style={styles.miniLabel}>説明・簡体字</label>
                    <input style={styles.miniInput} value={form.descZhCN}
                      onChange={e => setForm({...form, descZhCN: e.target.value})} placeholder="简体中文" />
                  </div>
                  <div style={styles.miniField}>
                    <label style={styles.miniLabel}>説明・繁体字</label>
                    <input style={styles.miniInput} value={form.descZhTW}
                      onChange={e => setForm({...form, descZhTW: e.target.value})} placeholder="繁體中文" />
                  </div>
                  <div style={styles.miniField}>
                    <label style={styles.miniLabel}>説明・韓国語</label>
                    <input style={styles.miniInput} value={form.descKo}
                      onChange={e => setForm({...form, descKo: e.target.value})} placeholder="한국어" />
                  </div>
                </div>
              </div>
            )}

            <div style={styles.row}>
              <div style={styles.field}>
                <label style={styles.label}>メニュー番号</label>
                <div style={{display:"flex", alignItems:"center", gap:8}}>
                  <input style={{...styles.input, width:80}} type="number" value={form.menuNumber}
                    onChange={e => setForm({...form, menuNumber: e.target.value})}
                    placeholder="自動" />
                  <span style={{fontSize:12, color:"#999"}}>空欄で自動採番</span>
                </div>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>価格（円・税込）</label>
                <input style={styles.input} type="number" value={form.price}
                  onChange={e => setForm({...form, price: e.target.value})}
                  placeholder="例：800" required />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>ステータス</label>
                <select style={styles.input} value={form.status}
                  onChange={e => setForm({...form, status: e.target.value})}>
                  <option value="available">在庫あり</option>
                  <option value="soldout">売り切れ</option>
                </select>
              </div>
            </div>

            {isStandard && categories.length > 0 && (
              <div style={styles.field}>
                <label style={styles.label}>カテゴリ</label>
                <select style={styles.input} value={form.categoryId || ''}
                  onChange={e => setForm({...form, categoryId: e.target.value})}>
                  <option value="">カテゴリなし</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.nameJa}</option>
                  ))}
                </select>
              </div>
            )}

            <div style={styles.field}>
              <label style={styles.label}>写真</label>
              <div style={styles.photoUpload}>
                <input type="file" accept="image/*" ref={fileInputRef}
                  onChange={handleImageUpload}
                  style={{ display: 'none' }} disabled={uploading} />
                {uploading ? (
                  <div style={styles.uploadingBox}>⏳ アップロード中...</div>
                ) : imagePreview ? (
                  <div style={styles.previewWrap}>
                    <img src={imagePreview} alt="preview" style={styles.preview} />
                    <div style={styles.previewInfo}>
                      <p style={styles.uploadedText}><CheckCircle size={14} style={{display:'inline',verticalAlign:'middle',marginRight:4,color:'#2d6a4f'}} />アップロード完了</p>
                      <button type="button" style={styles.btnRemove}
                        onClick={() => {
                          setImagePreview('');
                          setForm(prev => ({ ...prev, imageUrl: '' }));
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}>写真を削除</button>
                      <button type="button" style={styles.btnChange}
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}>
                        写真を変更
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={styles.uploadArea}
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}>
                    <span style={styles.uploadIcon}>📷</span>
                    <span style={styles.uploadText}>タップして写真を選択</span>
                    <span style={styles.photoHint}>最大200KB・800×600pxに自動圧縮</span>
                  </div>
                )}
              </div>
            </div>

            <div style={styles.btnRow}>
              <button type="button" onClick={handleTranslate}
                style={styles.btnTranslate} disabled={translating}>
                {translating ? '翻訳中...' : <><Globe size={14} style={{display:'inline',verticalAlign:'middle',marginRight:4}} />{isStandard ? '5言語自動翻訳' : '英語に翻訳'}</>}
              </button>
              <button type="submit" style={styles.btnPrimary} disabled={loading || uploading}>
                {loading ? '保存中...' : editingId ? <><Check size={14} style={{display:'inline',verticalAlign:'middle',marginRight:4}} />更新する</> : '+ 追加する'}
              </button>
              {editingId && (
                <button type="button" onClick={handleCancelEdit} style={styles.btnOutline}>
                  キャンセル
                </button>
              )}
            </div>
          </form>
        </div>

        {/* QRコード（店舗全体） */}
        <div style={styles.qrBox}>
          <h3 style={styles.subHeading}>QRコード（店舗全体）</h3>
          <p style={styles.urlText}>メニューURL：<strong>{menuUrl}</strong></p>
          <button onClick={() => setShowQR(!showQR)} style={styles.btnPrimary}>
            {showQR ? 'QRを隠す' : 'QRコードを表示'}
          </button>
          {showQR && (
            <div style={styles.qrWrap}>
              <QRCode value={menuUrl} size={200} />
              <button onClick={() => window.print()} style={styles.btnOutline}><Printer size={14} style={{display:"inline",verticalAlign:"middle",marginRight:4}} />印刷</button>
            </div>
          )}
        </div>

        {/* メニュー一覧 */}
        <h3 style={styles.subHeading}>メニュー一覧（{menus.length}品）</h3>
        <div style={styles.menuList}>
          {menus.map((menu, index) => {
            const cat = categories.find(c => c.id === menu.categoryId);
            return (
              <div key={menu.id} style={{
                ...styles.menuItem,
                border: editingId === menu.id ? '2px solid #2d6a4f' : '2px solid transparent'
              }}>
                <div style={styles.movebtnsCol}>
                  <button onClick={() => moveMenu(index, -1)} disabled={index === 0} style={styles.btnMove}>↑</button>
                  <button onClick={() => moveMenu(index, 1)} disabled={index === menus.length - 1} style={styles.btnMove}>↓</button>
                </div>
                {menu.imageUrl ? (
                  <img src={menu.imageUrl} alt={menu.nameJa} style={styles.menuThumb} />
                ) : null}
                <div style={styles.menuInfo}>
                  <div>
                    <span style={{fontSize:12, fontWeight:700, color:"#2d6a4f", marginRight:6}}>No.{menu.menuNumber || "—"}</span><span style={styles.menuName}>{menu.nameJa}</span>
                    {menu.nameEn ? <span style={styles.menuEn}> / {menu.nameEn}</span> : null}
                  </div>
                  {cat && <span style={styles.catTag}>{cat.nameJa}</span>}
                  {isStandard && (menu.nameZhCN || menu.nameZhTW || menu.nameKo) ? (
                    <div style={styles.langRow}>
                      {menu.nameZhCN ? <span style={styles.langTag}>簡: {menu.nameZhCN}</span> : null}
                      {menu.nameZhTW ? <span style={styles.langTag}>繁: {menu.nameZhTW}</span> : null}
                      {menu.nameKo ? <span style={styles.langTag}>韓: {menu.nameKo}</span> : null}
                    </div>
                  ) : null}
                  {menu.descJa ? <p style={styles.desc}>{menu.descJa}</p> : null}
                  <div style={styles.metaRow}>
                    <span style={styles.menuPrice}>¥{Number(menu.price).toLocaleString()}</span>
                    <span style={menu.status === 'soldout' ? styles.soldout : styles.available}>
                      {menu.status === 'soldout' ? '売切' : '在庫あり'}
                    </span>
                    <span style={styles.soldCount}>本日: {menu.soldCount || 0}食</span>
                  </div>
                </div>
                <div style={styles.menuActions}>
                  <button onClick={() => handleSoldCount(menu)} style={styles.btnSmall}>+1</button>
                  <button onClick={() => { if ((menu.soldCount || 0) > 0) { updateDoc(doc(db, "stores", currentUser.uid, "menus", menu.id), { soldCount: (menu.soldCount || 0) - 1 }); fetchData(); } }} disabled={(menu.soldCount || 0) === 0} style={{...styles.btnSmall, background: "#edf2f7", color: "#4a5568"}}>-1</button>
                  <button onClick={() => handleEdit(menu)} style={styles.btnEdit}>編集</button>
                  <button onClick={() => handleStatus(menu)} style={styles.btnSmall}>
                    {menu.status === 'available' ? '売切' : '在庫↩'}
                  </button>
                  <button onClick={() => handleDelete(menu.id)} style={styles.btnDanger}>削除</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', background: '#f8f9fa',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Hiragino Sans", sans-serif' },
  nav: { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 16px', background: '#fff', borderBottom: '1px solid #eee',
    position: 'sticky', top: 0, zIndex: 10 },
  logo: { margin: 0, color: '#2d6a4f', fontSize: '18px', fontWeight: '700' },
  content: { padding: '20px 16px 40px', maxWidth: '900px', margin: '0 auto' },
  heading: { fontSize: '20px', color: '#1a1a2e', margin: '0 0 16px', fontWeight: '700' },
  subHeading: { fontSize: '16px', color: '#1a1a2e', margin: '20px 0 12px', fontWeight: '700' },
  section: { background: '#fff', borderRadius: '12px', marginBottom: '16px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 16px', borderBottom: '0.5px solid #eee' },
  sectionTitle: { fontSize: '15px', fontWeight: '700', color: '#1a1a2e', margin: 0,
    display: 'flex', alignItems: 'center', gap: '8px' },
  sectionBody: { padding: '16px' },
  btnToggle: { padding: '6px 12px', background: 'transparent', border: '1px solid #ddd',
    borderRadius: '8px', cursor: 'pointer', fontSize: '12px', color: '#666' },
  planTag: { fontSize: '11px', background: '#f4a261', color: '#fff',
    padding: '2px 8px', borderRadius: '10px', fontWeight: '400' },
  lockedBox: { background: '#fafafa', border: '1px dashed #ddd', borderRadius: '8px',
    padding: '14px', fontSize: '13px', color: '#999',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    flexWrap: 'wrap', gap: '8px' },
  catList: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' },
  catItem: { display: 'flex', alignItems: 'center', gap: '8px',
    padding: '10px 12px', background: '#f8f9fa', borderRadius: '8px' },
  catInfo: { flex: 1 },
  catName: { fontSize: '14px', color: '#1a1a2e', margin: 0, fontWeight: '500' },
  catTrans: { fontSize: '11px', color: '#999', margin: '2px 0 0' },
  tableList: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' },
  tableItem: { display: 'flex', alignItems: 'flex-start', gap: '8px',
    padding: '12px', background: '#f8f9fa', borderRadius: '8px', flexWrap: 'wrap' },
  tableInfo: { flex: 1, minWidth: '160px' },
  tableNameRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  tableName: { fontSize: '14px', color: '#1a1a2e', fontWeight: '500' },
  tableEditRow: { display: 'flex', gap: '6px', alignItems: 'center' },
  tableInput: { flex: 1, padding: '6px 10px', border: '1px solid #ddd',
    borderRadius: '6px', fontSize: '13px' },
  tableUrl: { fontSize: '11px', color: '#bbb', margin: '4px 0 0', wordBreak: 'break-all' },
  tableActions: { display: 'flex', gap: '6px', flexShrink: 0 },
  qrDisplay: { background: '#fff', border: '1px solid #d8f3dc', borderRadius: '12px',
    padding: '20px', textAlign: 'center', marginTop: '12px',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' },
  qrLabel: { fontSize: '14px', fontWeight: '700', color: '#2d6a4f', margin: 0 },
  qrUrl: { fontSize: '11px', color: '#bbb', margin: 0, wordBreak: 'break-all' },
  addRow: { display: 'flex', gap: '8px', marginBottom: '8px' },
  hint: { fontSize: '11px', color: '#bbb', margin: 0 },
  form: { background: '#fff', padding: '16px', borderRadius: '12px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: '16px' },
  row: { display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' },
  field: { flex: 1, minWidth: '200px', marginBottom: '12px' },
  label: { display: 'block', marginBottom: '6px', fontSize: '13px', color: '#555' },
  input: { width: '100%', padding: '10px', border: '1px solid #ddd',
    borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '10px', border: '1px solid #ddd',
    borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical' },
  multiLangBox: { background: '#f0faf4', border: '1px solid #b7e4c7',
    borderRadius: '10px', padding: '12px 14px', marginBottom: '12px' },
  multiLangTitle: { fontSize: '13px', color: '#2d6a4f', fontWeight: '700', margin: '0 0 10px' },
  multiLangGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '8px' },
  miniField: { display: 'flex', flexDirection: 'column', gap: '4px' },
  miniLabel: { fontSize: '11px', color: '#555' },
  miniInput: { padding: '8px', border: '1px solid #ddd', borderRadius: '8px',
    fontSize: '13px', boxSizing: 'border-box', width: '100%' },
  photoUpload: { display: 'flex', flexDirection: 'column', gap: '8px' },
  uploadArea: { display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', gap: '6px', padding: '20px',
    border: '2px dashed #ddd', borderRadius: '12px', cursor: 'pointer',
    background: '#fafafa', textAlign: 'center' },
  uploadIcon: { fontSize: '28px' },
  uploadText: { fontSize: '14px', color: '#555', fontWeight: '500' },
  photoHint: { fontSize: '11px', color: '#bbb' },
  uploadingBox: { padding: '20px', textAlign: 'center', background: '#f8f9fa',
    borderRadius: '12px', color: '#f4a261', fontSize: '14px' },
  previewWrap: { display: 'flex', alignItems: 'center', gap: '16px',
    padding: '12px', background: '#f8f9fa', borderRadius: '12px' },
  preview: { width: '100px', height: '100px', objectFit: 'cover',
    borderRadius: '8px', border: '1px solid #eee', flexShrink: 0 },
  previewInfo: { display: 'flex', flexDirection: 'column', gap: '8px' },
  uploadedText: { color: '#2d6a4f', fontSize: '13px', margin: 0, fontWeight: '500' },
  btnRemove: { padding: '6px 12px', background: '#fee', color: '#c00',
    border: '1px solid #fcc', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' },
  btnChange: { padding: '6px 12px', background: '#f0f0f0', color: '#555',
    border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' },
  btnRow: { display: 'flex', gap: '10px', marginTop: '12px', flexWrap: 'wrap' },
  btnTranslate: { padding: '10px 16px', background: '#52b788', color: '#fff',
    border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' },
  btnPrimary: { padding: '10px 16px', background: '#2d6a4f', color: '#fff',
    border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap' },
  btnOutline: { padding: '10px 16px', border: '1.5px solid #2d6a4f', background: 'transparent',
    color: '#2d6a4f', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap' },
  btnUpgrade: { padding: '8px 14px', background: '#f4a261', color: '#fff',
    border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap' },
  btnSmall: { padding: '6px 10px', background: '#2d6a4f', color: '#fff',
    border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap' },
  btnSmallOutline: { padding: '5px 10px', background: 'transparent', color: '#666',
    border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' },
  btnEdit: { padding: '6px 10px', background: '#457b9d', color: '#fff',
    border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' },
  btnDanger: { padding: '6px 10px', background: '#e63946', color: '#fff',
    border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' },
  btnMove: { padding: '2px 8px', background: '#f0f0f0', border: '1px solid #ddd',
    borderRadius: '4px', cursor: 'pointer', fontSize: '12px', color: '#555', lineHeight: '1.4' },
  movebtns: { display: 'flex', flexDirection: 'column', gap: '2px', flexShrink: 0 },
  movebtnsCol: { display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0, paddingTop: '4px' },
  qrBox: { background: '#fff', padding: '16px', borderRadius: '12px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: '16px' },
  urlText: { fontSize: '13px', color: '#666', marginBottom: '12px', wordBreak: 'break-all' },
  qrWrap: { marginTop: '16px', display: 'flex', flexDirection: 'column',
    alignItems: 'center', gap: '12px' },
  menuList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  menuItem: { background: '#fff', padding: '12px 16px', borderRadius: '12px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex',
    alignItems: 'flex-start', gap: '10px', flexWrap: 'wrap' },
  menuThumb: { width: '60px', height: '60px', objectFit: 'cover',
    borderRadius: '8px', flexShrink: 0 },
  menuInfo: { flex: 1, minWidth: '160px' },
  menuName: { fontWeight: '700', color: '#1a1a2e', fontSize: '14px' },
  menuEn: { color: '#999', fontSize: '13px' },
  catTag: { display: 'inline-block', fontSize: '11px', background: '#d8f3dc',
    color: '#2d6a4f', padding: '2px 8px', borderRadius: '10px', marginTop: '4px' },
  langRow: { display: 'flex', gap: '6px', marginTop: '4px', flexWrap: 'wrap' },
  langTag: { fontSize: '11px', color: '#2d6a4f', background: '#d8f3dc',
    padding: '2px 6px', borderRadius: '4px' },
  desc: { fontSize: '12px', color: '#999', margin: '4px 0 0' },
  metaRow: { display: 'flex', alignItems: 'center', gap: '10px',
    marginTop: '6px', flexWrap: 'wrap' },
  menuPrice: { color: '#2d6a4f', fontWeight: '700', fontSize: '14px' },
  available: { background: '#d8f3dc', color: '#2d6a4f', padding: '2px 8px',
    borderRadius: '10px', fontSize: '11px' },
  soldout: { background: '#fee', color: '#c00', padding: '2px 8px',
    borderRadius: '10px', fontSize: '11px' },
  soldCount: { color: '#bbb', fontSize: '12px' },
  menuActions: { display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' },
  empty: { color: '#bbb', textAlign: 'center', fontSize: '13px', padding: '16px 0' },
};

export default MenuEditor;
